import { Isuccess } from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { FlyingMailClient } from '../../utils/flying-mail-client';

/**
 * Interface representing a mail user.
 */
export interface IMailUser {
  id: number;
  email: string;
  quotaBytes: number;
  messageLimit: number;
  createdAt: Date;
  expiresAt: Date | null;
  isActive: boolean;
  updatedAt: Date | null;
  companyId: number;
}

/**
 * Class representing a mail user.
 */
export class MailUser {
  /**
   * Base URL for mail user API endpoints.
   */
  static baseUrl = '/users';

  /**
   * The ID of the user.
   */
  id: number;

  /**
   * The email of the user.
   */
  email: string;

  /**
   * The quota bytes of the user.
   */
  quotaBytes: number;

  /**
   * The message limit of the user.
   */
  messageLimit: number;

  /**
   * The creation date of the user.
   */
  createdAt: Date;

  /**
   * The expiration date of the user.
   */
  expiresAt: Date | null;

  /**
   * Whether the user is active.
   */
  isActive: boolean;

  /**
   * The update date of the user.
   */
  updatedAt: Date | null;

  /**
   * The company ID of the user.
   */
  companyId: number;

  constructor(data: IMailUser) {
    this.id = data.id;
    this.email = data.email;
    this.quotaBytes = data.quotaBytes;
    this.messageLimit = data.messageLimit;
    this.createdAt = data.createdAt;
    this.expiresAt = data.expiresAt;
    this.isActive = data.isActive;
    this.updatedAt = data.updatedAt;
    this.companyId = data.companyId;
  }

  /**
   * Retrieves all mail users.
   *
   * @returns A Promise that resolves to an array of MailUser instances.
   */
  static async getAll() {
    const observer$ = FlyingMailClient.ehttp
      .makeGet<IMailUser[]>(`${MailUser.baseUrl}`);
    const data = await lastValueFrom(observer$);

    return data.map(val => new MailUser(val));
  }

  /**
   * Retrieves a specific mail user by email.
   *
   * @param email - The email of the user to retrieve.
   * @returns A Promise that resolves to a new MailUser instance.
   */
  static async getByEmail(email: string) {
    const observer$ = FlyingMailClient.ehttp
      .makeGet<IMailUser>(`${MailUser.baseUrl}/${email}`);
    const data = await lastValueFrom(observer$);

    return new MailUser(data);
  }

  /**
   * Adds a new mail user.
   *
   * @param payload - The payload to add the user with.
   * @returns A Promise that resolves to an Isuccess instance.
   */
  static add(payload: Omit<IMailUser, 'id' | 'companyId' |
    'createdAt' | 'updatedAt' | 'expiresAt' | 'isActive' |
    'quotaBytes' | 'messageLimit'>) {
    const observer$ = FlyingMailClient.ehttp
      .makePost<Isuccess>(`${MailUser.baseUrl}`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Updates a specific mail user.
   *
   * @param payload - The payload to update the user with.
   * @returns A Promise that resolves to an Isuccess instance.
   */
  update(payload: Omit<Partial<IMailUser>, 'id' | 'companyId' | 'createdAt' |
    'updatedAt' | 'expiresAt' | 'isActive' | 'quotaBytes' | 'messageLimit'>) {
    const observer$ = FlyingMailClient.ehttp
      .makePut<Isuccess>(`${MailUser.baseUrl}/${this.email}`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Removes the mail user.
   *
   * @returns A Promise that resolves to an Isuccess instance.
   */
  remove() {
    const observer$ = FlyingMailClient.ehttp
      .makeDelete<Isuccess>(`${MailUser.baseUrl}/${this.email}`);

    return lastValueFrom(observer$);
  }
}
